import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { billingAPI } from '../lib/api';
import { Button } from './ui/button';
import { AlertTriangle, Clock, X, CreditCard } from 'lucide-react';

/**
 * BetaSunsetBanner - Displays beta program ending notice
 * 
 * Display Logic:
 * - Show if current date < BETA_END_DATE (Feb 19, 2026)
 * - Hide if tenant has active Stripe subscription
 * - Hide if tenant is manually marked as beta_exempt
 * - Hide if user has dismissed the banner (persists in DB)
 */
export const BetaSunsetBanner = () => {
  const navigate = useNavigate();
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dismissing, setDismissing] = useState(false);

  const fetchStatus = useCallback(async () => {
    try {
      const response = await billingAPI.getBetaTrialStatus();
      setStatus(response.data);
    } catch (error) {
      console.error('Failed to fetch beta status:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchStatus();
  }, [fetchStatus]);

  const handleDismiss = async () => {
    setDismissing(true);
    try {
      await billingAPI.dismissBetaBanner('beta_sunset');
      setStatus(prev => prev ? { ...prev, show_beta_banner: false, is_beta_banner_dismissed: true } : prev);
    } catch (error) {
      console.error('Failed to dismiss banner:', error);
    } finally {
      setDismissing(false);
    }
  };

  const handleUpdateBilling = () => {
    navigate('/billing');
  };

  // Don't render if loading, no data, or banner shouldn't show
  if (loading || !status || !status.show_beta_banner) {
    return null;
  }

  const betaEndDate = status.beta_end_date || '2026-02-19';
  const formattedDate = new Date(betaEndDate + 'T00:00:00').toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });

  return (
    <div 
      className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-4"
      data-testid="beta-sunset-banner"
    >
      <div className="flex items-start gap-3">
        <div className="flex-shrink-0">
          <AlertTriangle className="w-5 h-5 text-amber-600" />
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="text-sm font-semibold text-amber-800">
            Stock & Load Beta Program Concluding
          </h3>
          <p className="mt-1 text-sm text-amber-700">
            The Stock & Load beta program will officially conclude on{' '}
            <span className="font-medium">{formattedDate}</span>.
            To ensure uninterrupted access to your account and data, please update your 
            billing information and activate a subscription before this date.
          </p>
          <div className="mt-3 flex flex-wrap items-center gap-2">
            <Button
              size="sm"
              onClick={handleUpdateBilling}
              className="bg-amber-600 hover:bg-amber-700 text-white"
              data-testid="beta-banner-update-billing-btn"
            >
              <CreditCard className="w-4 h-4 mr-1.5" />
              Update Billing
            </Button>
            <Button
              size="sm"
              variant="ghost"
              onClick={handleDismiss}
              disabled={dismissing}
              className="text-amber-700 hover:text-amber-800 hover:bg-amber-100"
              data-testid="beta-banner-dismiss-btn"
            >
              {dismissing ? 'Dismissing...' : 'Dismiss'}
            </Button>
          </div>
        </div>
        <button
          onClick={handleDismiss}
          disabled={dismissing}
          className="flex-shrink-0 p-1 rounded hover:bg-amber-100 text-amber-600 hover:text-amber-800 transition-colors"
          aria-label="Dismiss banner"
          data-testid="beta-banner-close-btn"
        >
          <X className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
};

/**
 * TrialCountdownBanner — thin sticky bar below the app header
 */
export const TrialCountdownBanner = () => {
  const navigate = useNavigate();
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchStatus = useCallback(async () => {
    try {
      const response = await billingAPI.getBetaTrialStatus();
      setStatus(response.data);
    } catch (error) {
      console.error('Failed to fetch trial status:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchStatus();
  }, [fetchStatus]);

  const handleActivate = () => {
    navigate('/billing');
  };

  if (loading || !status || !status.show_trial_banner) {
    return null;
  }

  const { trial_days_remaining, trial_expired } = status;
  const isUrgent = trial_days_remaining !== null && trial_days_remaining < 7;
  const isExpired = trial_expired;

  const barBg = isExpired
    ? 'bg-red-50 dark:bg-red-950/40 border-red-200 dark:border-red-800'
    : isUrgent
      ? 'bg-orange-50 dark:bg-orange-950/40 border-orange-200 dark:border-orange-800'
      : 'bg-muted/60 border-border';

  const textColor = isExpired
    ? 'text-red-700 dark:text-red-300'
    : isUrgent
      ? 'text-orange-700 dark:text-orange-300'
      : 'text-muted-foreground';

  const daysText = trial_days_remaining === 0
    ? 'less than 1 day'
    : `${trial_days_remaining} day${trial_days_remaining !== 1 ? 's' : ''}`;

  const label = isExpired
    ? 'Trial Ended \u2014 please activate to continue'
    : `Free Trial \u2022 ${daysText} remaining`;

  const labelShort = isExpired
    ? 'Trial Ended'
    : `Free Trial \u2022 ${daysText} left`;

  return (
    <div
      className={`sticky top-14 z-20 border-b ${barBg}`}
      data-testid="trial-countdown-banner"
    >
      <div className="max-w-[calc(100vw-16rem)] ml-auto flex items-center justify-between px-4 md:px-6 h-9 text-xs">
        <span className={`flex items-center gap-1.5 ${textColor} truncate`}>
          <Clock className="w-3.5 h-3.5 shrink-0" />
          <span className="hidden sm:inline">{label}</span>
          <span className="sm:hidden">{labelShort}</span>
        </span>
        <Button
          size="sm"
          onClick={handleActivate}
          className="h-7 text-xs ml-3 shrink-0"
          data-testid="trial-banner-activate-btn"
        >
          <CreditCard className="w-3 h-3 mr-1" />
          {isExpired ? 'Activate' : 'Activate Plan'}
        </Button>
      </div>
    </div>
  );
};

/**
 * BillingBanners - Combined component that shows both banners as needed
 * Use this in the Dashboard/Layout to show all relevant billing banners
 */
export const BillingBanners = () => {
  return (
    <div className="space-y-0" data-testid="billing-banners">
      <BetaSunsetBanner />
      <TrialCountdownBanner />
    </div>
  );
};

export default BillingBanners;
